import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { FieldError } from "~/components/field-error";
import { api, APIError } from "~/lib/api";
import { toNum } from "~/lib/format";
import type { IParcel } from "~/lib/proto";
import { cn } from "~/lib/utils";

const schema = z.object({
  freighterCost: z.coerce.number().min(0, "Enter the freighter cost"),
  deliveryFeeKes: z.coerce.number().min(0, "Enter the delivery fee, or 0"),
  amountChargedKes: z.coerce.number().positive("Enter what the customer pays"),
  chargeableWeightG: z.coerce.number().min(0).optional(),
  weightKg: z.coerce.number().min(0).optional(),
  lengthCm: z.coerce.number().min(0).optional(),
  widthCm: z.coerce.number().min(0).optional(),
  heightCm: z.coerce.number().min(0).optional(),
  category: z.string().optional(),
});
type Values = z.infer<typeof schema>;

type Estimate = { state: "idle" } | { state: "filled" } | { state: "error"; message: string };

function kes(amount: number): string {
  return `KES ${Math.round(amount).toLocaleString("en-KE")}`;
}

export function PricingDialog({
  parcel,
  open,
  onOpenChange,
}: {
  parcel: IParcel;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [computing, setComputing] = useState(false);
  const [estimate, setEstimate] = useState<Estimate>({ state: "idle" });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { freighterCost: 0, deliveryFeeKes: 0, amountChargedKes: 0, chargeableWeightG: 0, weightKg: 0, lengthCm: 0, widthCm: 0, heightCm: 0, category: "" },
  });

  const savedFee = toNum(parcel.deliveryFeeKes);
  useEffect(() => {
    if (!open) return;
    form.setValue("deliveryFeeKes", Math.round(savedFee / 100));
    setEstimate({ state: "idle" });
    setSubmitError(null);
  }, [open, savedFee, form]);

  const freighter = Number(form.watch("freighterCost")) || 0;
  const delivery = Number(form.watch("deliveryFeeKes")) || 0;
  const amount = Number(form.watch("amountChargedKes")) || 0;
  const margin = amount - freighter - delivery;
  const customer = parcel.customerName || "The customer";

  async function fillFromRate() {
    const weightKg = Number(form.getValues("weightKg")) || 0;
    const lengthCm = Number(form.getValues("lengthCm")) || 0;
    const widthCm = Number(form.getValues("widthCm")) || 0;
    const heightCm = Number(form.getValues("heightCm")) || 0;
    if (weightKg <= 0 && !(lengthCm > 0 && widthCm > 0 && heightCm > 0)) {
      setEstimate({ state: "error", message: "Enter a weight, or all three box sizes." });
      return;
    }
    setComputing(true);
    try {
      const quote = await api.quote({ originCountry: parcel.originCountry ?? "", mode: parcel.mode ?? "", weightKg, lengthCm, widthCm, heightCm });
      form.setValue("amountChargedKes", Math.round(toNum(quote.estimateKes) / 100) + (Number(form.getValues("deliveryFeeKes")) || 0));
      form.setValue("freighterCost", Math.round(toNum(quote.freighterCostKes) / 100));
      if (quote.chargeableWeightG) form.setValue("chargeableWeightG", toNum(quote.chargeableWeightG));
      setEstimate({ state: "filled" });
    } catch (e) {
      setEstimate({ state: "error", message: e instanceof APIError ? e.message : "The rate could not be worked out. Enter the amounts yourself." });
    } finally {
      setComputing(false);
    }
  }

  const submit = form.handleSubmit(async (v) => {
    setBusy(true);
    setSubmitError(null);
    try {
      await api.ops.receivedKenya(parcel.id!, {
        freighterCost: Math.round(v.freighterCost * 100),
        deliveryFeeKes: Math.round(v.deliveryFeeKes * 100),
        amountChargedKes: Math.round(v.amountChargedKes * 100),
        chargeableWeightG: v.chargeableWeightG ? Math.round(v.chargeableWeightG) : undefined,
        lengthMm: v.lengthCm ? Math.round(v.lengthCm * 10) : undefined,
        widthMm: v.widthCm ? Math.round(v.widthCm * 10) : undefined,
        heightMm: v.heightCm ? Math.round(v.heightCm * 10) : undefined,
        category: v.category?.trim() || undefined,
      });
      toast.success(`${customer} was asked to pay ${kes(v.amountChargedKes)}`);
      void qc.invalidateQueries({ queryKey: ["ops", "parcels"] });
      void qc.invalidateQueries({ queryKey: ["parcel", parcel.id] });
      onOpenChange(false);
      form.reset();
    } catch (e) {
      setSubmitError(e instanceof APIError ? e.message : "The amount was not saved. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92svh] overflow-y-auto sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Landed in Kenya: set the amount due</DialogTitle>
          <DialogDescription>{customer} gets an SMS asking for this amount as soon as you confirm. All values are in KES.</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit} noValidate>
          <fieldset className="bg-muted/40 space-y-2.5 rounded-md p-3">
            <legend className="sr-only">Measure the parcel</legend>
            <p className="text-[13px] font-medium">Measure it</p>
            <div className="grid grid-cols-4 gap-2">
              <div className="space-y-1.5"><Label htmlFor="weightKg">Weight kg</Label><Input id="weightKg" type="number" inputMode="decimal" {...form.register("weightKg")} /></div>
              <div className="space-y-1.5"><Label htmlFor="lengthCm">Length cm</Label><Input id="lengthCm" type="number" inputMode="decimal" {...form.register("lengthCm")} /></div>
              <div className="space-y-1.5"><Label htmlFor="widthCm">Width cm</Label><Input id="widthCm" type="number" inputMode="decimal" {...form.register("widthCm")} /></div>
              <div className="space-y-1.5"><Label htmlFor="heightCm">Height cm</Label><Input id="heightCm" type="number" inputMode="decimal" {...form.register("heightCm")} /></div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="secondary" size="sm" loading={computing} onClick={() => void fillFromRate()}>
                Fill in from the freighter rate
              </Button>
              {estimate.state === "filled" && <span className="text-text-faint text-[12px]">Filled in below. Adjust if needed.</span>}
            </div>
            {estimate.state === "error" && <FieldError>{estimate.message}</FieldError>}
          </fieldset>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="freighter">Freighter cost</Label>
              <Input id="freighter" type="number" inputMode="numeric" {...form.register("freighterCost")} />
              <FieldError>{form.formState.errors.freighterCost?.message}</FieldError>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="delivery">Delivery fee</Label>
              <Input id="delivery" type="number" inputMode="numeric" {...form.register("deliveryFeeKes")} />
              <FieldError>{form.formState.errors.deliveryFeeKes?.message}</FieldError>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="amount">Customer pays, delivery fee included</Label>
            <Input id="amount" type="number" inputMode="numeric" className="h-10 text-[16px] font-semibold" {...form.register("amountChargedKes")} />
            <FieldError>{form.formState.errors.amountChargedKes?.message}</FieldError>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="weight">Chargeable weight g</Label>
              <Input id="weight" type="number" inputMode="numeric" {...form.register("chargeableWeightG")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="category">Category</Label>
              <Input id="category" placeholder="Electronics" {...form.register("category")} />
            </div>
          </div>

          <dl className="border-hairline space-y-1.5 rounded-md border p-3 text-[13px]">
            <SummaryRow label="Customer pays" value={kes(amount)} />
            <SummaryRow label="Freighter cost" value={`− ${kes(freighter)}`} faint />
            <SummaryRow label="Delivery fee" value={`− ${kes(delivery)}`} faint />
            <div className="border-hairline flex items-center justify-between border-t pt-1.5">
              <dt className="font-medium">Your margin</dt>
              <dd className={cn("tabular font-semibold", margin < 0 && "text-destructive")}>
                {kes(margin)}
                {amount > 0 && <span className="text-text-faint ml-1.5 font-normal">{Math.round((margin / amount) * 100)}%</span>}
              </dd>
            </div>
            {amount > 0 && margin < 0 && (
              <p role="alert" className="text-destructive text-[12px]">
                This is below your cost. You would lose {kes(-margin)} on this parcel.
              </p>
            )}
          </dl>

          {submitError && <FieldError>{submitError}</FieldError>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" loading={busy}>
              {amount > 0 ? `Ask for ${kes(amount)}` : "Set the amount due"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SummaryRow({ label, value, faint }: { label: string; value: string; faint?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-text-faint">{label}</dt>
      <dd className={cn("tabular", faint && "text-text-faint")}>{value}</dd>
    </div>
  );
}
